import React, { useEffect, useState } from 'react';
import SEO from '../components/SEO';
import '../styles.css';

const faqData = [
  {
    question: "Who created Virgil?",
    answer: "Virgil was built with Emmy-winning television and film writers and creative executives from major Hollywood production companies, so it's actually useful for working writers."
  },
  {
    question: "What AI models does Virgil use?",
    answer: "Virgil includes Claude Opus 5.5, GPT-6.1 Sol, Gemini 3.1 Pro, Grok 4.7, and open models such as DeepSeek V4 Pro and Kimi K3. Opus 5.5 and GPT-6.1 Sol are the defaults for their providers. GPT-6.1 Sol Ultra means Sol with the highest reasoning effort, shown as Maximum in the app. Enable specific model selection in Settings to choose alternatives such as GPT-6 Astra, GPT-6 Luna, Claude Sonnet 5.5, or Fable 5.1. Image, video, and voice tools have their own model menus, including Nano Banana 2.1, FLUX 3 Image, Ideogram 4.5 Precise Edit, Gemini Omni 1.1 Flash, and Gemini 3.8 Flash TTS."
  },
  {
    question: "Is Virgil available now?",
    answer: "Yes. Public-beta signup is open while the 100 seats remain. Create an account, verify your email, choose a plan, and start the trial through Stripe."
  },
  {
    question: "How does pricing work?",
    answer: "The public beta starts with a card-backed 14-day trial and up to $20 of included AI usage. Trial AI usage stops at that limit, with no overage. Unless you cancel, your selected Starter, Pro, or Studio plan begins after day 14. Paid plans include monthly AI credit, and usage beyond the paid allowance may be metered at the rates shown at checkout."
  },
  {
    question: "What happens if I use the full $20 during the trial?",
    answer: "AI model usage stops for the rest of the trial, so you cannot incur trial overage. Reaching the cap does not charge your card early. Your selected monthly plan still starts after Stripe's 14-day trial period unless you cancel."
  },
  {
    question: "What makes Virgil different from other AI writing tools?",
    answer: "Most AI writers treat your script like generic text. Virgil is built around screenwriting: real screenplay formatting, an autonomous agent mode, support for multiple AI models (GPT, Claude, Gemini, and Grok), an understanding of screenplay structure, script coverage, and editing that knows the difference between action and dialogue."
  },
  {
    question: "Is Virgil a good AI writer for beginners?",
    answer: "Yes. Virgil works for writers at any level, from first-time AI users to working screenwriters. You get clear guidance, professional formatting, and suggestions that match how experienced you are."
  },
  {
    question: "Can I use Virgil as an AI writer for novels and books?",
    answer: "Yes. Virgil is strong on screenplays, but it also works for novels, books, and long-form fiction. Supported formats include novels, TV scripts, film screenplays, and podcasts."
  },
  {
    question: "Can Virgil convert books or other content into screenplays?",
    answer: "Yes. Virgil can take long-form fiction or non-fiction and adapt it into a properly formatted feature-length script while keeping the source material's intent intact."
  },
  {
    question: "How does the autonomous agent mode work?",
    answer: "In autonomous mode, you can give Virgil complex tasks like 'Write Act 2' or 'Revise all dialogue for this character.' Virgil will work independently, providing real-time progress updates as it completes your request."
  },
  {
    question: "Is my work secure and private?",
    answer: "Your projects are associated with your account and are not sold. Virgil sends prompts and relevant project context to the AI provider you choose so it can generate a response. We use access controls and encryption in transit; see the Privacy Policy for providers and retention details."
  },
  {
    question: "Can I collaborate with others using Virgil?",
    answer: "Currently, Virgil is designed for individual use, but collaboration features are on our roadmap for future updates."
  },
  {
    question: "What file formats does Virgil support?",
    answer: "Virgil works with standard screenplay formats and can export to PDF, Final Draft, and other industry-standard formats."
  }
];

function FAQPage() {
  const [showSupportForm, setShowSupportForm] = useState(false);
  const [supportForm, setSupportForm] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState(null); // 'success' | 'error' | null
  const [formError, setFormError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSupportInputChange = (e) => {
    const { name, value } = e.target;
    setSupportForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSupportSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!supportForm.name || !supportForm.email || !supportForm.message) {
      setFormStatus('error');
      setFormError('Name, email, and message are required.');
      return;
    }

    setIsSubmitting(true);
    try {
      const resp = await fetch('/api/support', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'support',
          name: supportForm.name,
          email: supportForm.email,
          message: supportForm.message
        })
      });

      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data?.success !== true) {
        const errMsg = (data && (data.error || data.message)) || 'Failed to send support request.';
        setFormStatus('error');
        setFormError(errMsg);
        return;
      }

      setFormStatus('success');
      setSupportForm({ name: '', email: '', message: '' });
    } catch (err) {
      setFormStatus('error');
      setFormError('Network or server error.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-container">
      <SEO 
        title="FAQ - Virgil AI Writer | Questions About AI Screenwriting Software"
        description="Common questions about Virgil AI writer. Learn about our AI writing assistant, pricing, features, and how it helps screenwriters and authors create better content."
        keywords="AI writer FAQ, AI writing assistant questions, screenwriting software help, AI script writer info"
      />
      {/* Main Content */}
      <section className="content-section faq-section faq-page-section static-page-section">
        <h1 className="page-title page-title--wide faq-title">Frequently Asked Questions</h1>
        <p className="page-lead page-lead--compact faq-page-lead">
          Common questions from writers exploring Virgil for the first time.
        </p>
        
        <div className="faq-list faq-list-tight">
          {faqData.map((item, index) => (
            <details 
              key={index} 
              className="faq-item"
            >
              <summary>{item.question}</summary>
              <p>{item.answer}</p>
            </details>
          ))}
        </div>

        <div className="faq-contact">
          <h3>Still have questions?</h3>
          <p>We're here to help! Reach out to our support team.</p>
          <button
            type="button"
            className="cta-button"
            onClick={() => setShowSupportForm(true)}
          >
            Contact Support
          </button>

          {showSupportForm && (
            <div className="faq-support-form-wrap">
              <form onSubmit={handleSupportSubmit} className="faq-support-form">
                <div className="form-field">
                  <label htmlFor="support-name">Name</label>
                  <input
                    id="support-name"
                    name="name"
                    type="text"
                    value={supportForm.name}
                    onChange={handleSupportInputChange}
                    placeholder="Your full name"
                    autoComplete="name"
                    required
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="support-email">Email</label>
                  <input
                    id="support-email"
                    name="email"
                    type="email"
                    value={supportForm.email}
                    onChange={handleSupportInputChange}
                    placeholder="you@company.com"
                    autoComplete="email"
                    required
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="support-message">How can we help?</label>
                  <textarea
                    id="support-message"
                    name="message"
                    rows="4"
                    value={supportForm.message}
                    onChange={handleSupportInputChange}
                    placeholder="Describe the issue or question..."
                    required
                  />
                </div>

                <div className="faq-support-actions">
                  <button type="submit" className="cta-button" disabled={isSubmitting}>
                    {isSubmitting ? 'Sending…' : 'Send to Support'}
                  </button>
                  <button
                    type="button"
                    className="cta-button secondary"
                    onClick={() => {
                      setShowSupportForm(false);
                      setFormStatus(null);
                      setFormError('');
                    }}
                  >
                    Cancel
                  </button>
                </div>

                {formStatus === 'success' && (
                  <div className="form-success faq-support-status" role="status">
                    Thank you! Your message has been sent.
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="form-error faq-support-status" role="alert">
                    {formError || 'Something went wrong.'}
                  </div>
                )}
              </form>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default FAQPage;